import React, {
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Root, Trigger, Content, Portal } from "@radix-ui/react-popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "utils/cn";
import { useController, useFormContext } from "react-hook-form";
import { InputV3 } from "../input/InputV3";

export interface MultiSelectOption {
  value: string | number;
  label: string;
  description?: string;
}

export interface MultiSelectDropdownProps {
  name: string;
  label?: string;
  options: MultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  onChange?: (value: (string | number)[]) => void;
  value?: (string | number)[];
  noFormContext?: boolean;
  triggerClassName?: string;
}

export const MultiSelectDropdown = forwardRef<{ clear: () => void }, MultiSelectDropdownProps>(
  (
    {
      name,
      label,
      options,
      placeholder = "Select options",
      searchPlaceholder = "Search...",
      disabled = false,
      onChange: externalOnChange,
      value: externalValue,
      noFormContext = false,
      triggerClassName = "",
    },
    ref
  ) => {
    // State for managing the component
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Refs
    const inputRef = useRef<HTMLInputElement>(null);
    const _containerRef = useRef<HTMLDivElement>(null);

    // Form context integration
    const formContext = useFormContext();
    const controller = noFormContext
      ? null
      : useController({
          name,
          control: formContext?.control,
        });

    // Get value and onChange handler based on whether we're using form context
    const value = noFormContext ? externalValue || [] : controller?.field.value || [];
    const onChange = noFormContext ? externalOnChange : controller?.field.onChange;
    const _error = noFormContext ? undefined : controller?.fieldState.error;

    // Expose clear function via ref
    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          if (onChange) {
            onChange([]);
          }
          setSearchTerm("");
          setHighlightedIndex(-1);
        },
      }),
      [onChange]
    );

    // Filter options based on search term
    const filteredOptions = searchTerm
      ? options.filter(
          (option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(option.value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;

    // Get display text for selected values
    const getSelectedText = () => {
      if (!value || value.length === 0) return placeholder;
      if (value.length === 1) {
        const option = options.find((option) => option.value === value[0]);
        return option ? option.label : placeholder;
      }
      return `${value.length} projects selected`;
    };

    // Handle option selection
    const handleSelectOption = (optionValue: string | number) => {
      if (!onChange) return;

      const newValue = value.includes(optionValue)
        ? value.filter((v: string | number) => v !== optionValue)
        : [...value, optionValue];

      onChange(newValue);
    };

    // Handle search input
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setHighlightedIndex(-1);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
      } else if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        handleSelectOption(filteredOptions[highlightedIndex].value);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };

    // Reset state when dropdown opens
    useEffect(() => {
      if (open) {
        setSearchTerm("");
        setHighlightedIndex(-1);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }, [open]);

    return (
      <Root open={open} onOpenChange={setOpen}>
        <div className="flex flex-col gap-2">
          {label && <label className="text-foreground font-medium text-sm">{label}</label>}

          <Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "flex h-10 w-full items-center justify-between rounded-md border border-border bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                triggerClassName
              )}
            >
              <span
                className={cn(
                  "block truncate",
                  !value || value.length === 0 ? "text-muted-foreground" : "text-foreground"
                )}
              >
                {getSelectedText()}
              </span>
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </button>
          </Trigger>

          <Portal>
            <Content
              className="z-[999] w-[var(--radix-popover-trigger-width)] rounded-lg bg-card border border-border shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 overflow-hidden"
              sideOffset={5}
              align="start"
              alignOffset={0}
              avoidCollisions
              onInteractOutside={() => setOpen(false)}
              onWheel={(e) => e.stopPropagation()}
              style={{ pointerEvents: "auto" }}
            >
              <div className="flex flex-col max-h-[350px]">
                <div className="p-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                    <InputV3
                      ref={inputRef}
                      type="search"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={handleKeyDown}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="border-t border-border flex-shrink-0" />
                <div
                  role="listbox"
                  className="overflow-y-auto p-2 max-h-[240px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                      <button
                        key={option.value}
                        role="option"
                        aria-selected={value.includes(option.value)}
                        type="button"
                        className={cn(
                          "flex w-full items-center py-2 px-3 cursor-pointer rounded-md text-left transition-all duration-200",
                          "active:scale-95 group",
                          highlightedIndex === index
                            ? "bg-muted text-foreground ring-1 ring-ring"
                            : "",
                          value.includes(option.value)
                            ? "bg-selected text-selected-foreground font-medium"
                            : "text-foreground/90 hover:text-foreground hover:bg-muted"
                        )}
                        onClick={() => handleSelectOption(option.value)}
                        tabIndex={-1}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              "w-4 h-4 border rounded flex items-center justify-center",
                              value.includes(option.value)
                                ? "border-primary/60 bg-primary/20"
                                : "border-border"
                            )}
                          >
                            {value.includes(option.value) && (
                              <Check className="w-3 h-3 text-primary" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium">{option.label}</div>
                            {option.description && (
                              <div className="text-xs text-muted-foreground">
                                {option.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="py-8 text-center text-muted-foreground text-sm">
                      No projects found
                    </div>
                  )}
                </div>
              </div>
            </Content>
          </Portal>
        </div>
      </Root>
    );
  }
);

MultiSelectDropdown.displayName = "MultiSelectDropdown";
